/**
 * MessageSearchBar: barra di ricerca messaggi con navigazione risultati.
 *
 * Ordine: MessageList mostra come "1 di N" il risultato piu' recente (gira l'elenco
 * cronologico che riceve da ChatView). Quindi qui "piu' vecchio" = onNext (indice +1)
 * e "piu' recente" = onPrev (indice -1): freccia su = risultato precedente nella chat.
 */

import { useEffect, useRef } from 'react';
import { Search, ChevronUp, ChevronDown, X } from 'lucide-react';
import { iconTip } from '@/components/messages/ChatTooltip';

interface Props {
  query: string;
  onChange: (q: string) => void;
  totalResults: number;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

export function MessageSearchBar({ query, onChange, totalResults, currentIndex, onPrev, onNext, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F7F5] dark:bg-gray-900 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
      <Search className="w-4 h-4 text-[#787774] flex-shrink-0" />
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Cerca nei messaggi..."
        className="flex-1 text-sm bg-transparent outline-none text-[#37352F] dark:text-white placeholder:text-[#787774]"
        onKeyDown={(e) => {
          // Invio = risultato piu' vecchio (si risale la chat), Maiusc+Invio = piu' recente.
          if (e.key === 'Enter') { e.preventDefault(); if (e.shiftKey) onPrev(); else onNext(); }
          // preventDefault: Esc chiude solo la ricerca, non tutto il pannello Messaggi.
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
        }}
      />
      {query && (
        <span className="text-xs text-[#787774] flex-shrink-0">
          {totalResults === 0 ? 'Nessun risultato' : `${currentIndex + 1} di ${totalResults}`}
        </span>
      )}
      <button onClick={onNext} disabled={totalResults === 0}
        className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 disabled:opacity-30 transition-colors"
        {...iconTip('Risultato precedente (piu\' vecchio)')}>
        <ChevronUp className="w-4 h-4 text-[#787774]" />
      </button>
      <button onClick={onPrev} disabled={totalResults === 0}
        className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 disabled:opacity-30 transition-colors"
        {...iconTip('Risultato successivo (piu\' recente)')}>
        <ChevronDown className="w-4 h-4 text-[#787774]" />
      </button>
      <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
        {...iconTip('Chiudi la ricerca')}>
        <X className="w-4 h-4 text-[#787774]" />
      </button>
    </div>
  );
}
